import { useContext } from 'react';
import { useRecoilCallback } from 'recoil';
import { Contexts } from 'react-vtk-js';
import { angleBetweenVectors } from '@kitware/vtk.js/Common/Core/Math';
import type { Vector3 } from '@kitware/vtk.js/types';
import { useViewportId, useViewType, slicerBoundState, sizesState, scaleState } from './state';
import { calculateDefaultCameraParametersFromImageData, radiansToDegrees } from '../utils/math';
import { rotateFromImageCenter } from '../utils/rotateFromImageCenter';
import { viewportDisplayConfigurationSelector } from 'domains/viewer/ViewportsConfigurations';
import { vec3, mat4 } from 'gl-matrix';
import vtkCamera from '@kitware/vtk.js/Rendering/Core/Camera';
import vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import { useActiveSliceImage } from './useImage';
import { useViewCamera } from './useViewCamera';
import { DRE_MPR_VIEWPORT_TYPES } from 'config/constants';
import type { DreAllViewportTypes } from 'config/constants';
import clamp from 'ramda/es/clamp';
import { useImagingContext } from './imaging/ImagingContext';

type FlipOperation = // viewport should be flipped from its original orientation
  Nullable<true | false>; // toggle the current flip state

export const DEFAULT_ZOOM_VALUE = 1;

export const convertZoomAndParallelScale = (
  sizes: Nullable<{ width: number; height: number }>,
  image: vtkImageData,
  viewType: DreAllViewportTypes,
  value: number,
  mode: 'zoomToParallel' | 'parallelToZoom'
): number => {
  const [row, column] = image.getDimensions();
  const defaultZoomScale = sizes ? calculateZoomScale(sizes, { row, column }) : 1;
  const heightOfImage = image.getDimensions()[1] * image.getSpacing()[1];

  const isMip = viewType === 'THREE_D_MIP';
  // @ts-expect-error [EN-7967] - TS2345 - Argument of type 'DreAllViewportTypes' is not assignable to parameter of type '"AXIAL_DRE" | "CORONAL_DRE" | "SAGITTAL_DRE"'.
  const isMPR = DRE_MPR_VIEWPORT_TYPES.includes(viewType);
  const bounds = image.getBounds();
  const axialLength = bounds[5] - bounds[4];

  const vtkWorldUnit = isMip || isMPR ? axialLength : heightOfImage;

  return mode === 'zoomToParallel'
    ? (vtkWorldUnit / defaultZoomScale / 2) * value
    : (value * 2 * defaultZoomScale) / vtkWorldUnit;
};

export const calculateZoomScale = (
  sizes: { width: number; height: number },
  { row, column }: { row: number; column: number }
): number => {
  if (sizes.width === 0 || sizes.height === 0 || row === 0 || column === 0) {
    // either missing viewport data or image data
    return 1;
  }

  // when very narrow, tall
  // iff width < height, row < column
  if (sizes.width < sizes.height || row > column) {
    return clamp(0, 1, sizes.width / sizes.height / (row / column));
  }

  return 1;
};

export const useSetZoom = (): ((zoom: number) => Promise<void>) => {
  const viewportId = useViewportId();
  const view = useContext(Contexts.ViewContext);
  const setZoomConfiguration = useSetZoomConfiguration();
  const image = useActiveSliceImage();
  const viewType = useViewType();

  const setZoom = useRecoilCallback(
    ({ snapshot, set }) =>
      async (zoom: number) => {
        const camera = view?.getCamera();

        if (view == null || camera == null || image == null || viewportId == null) {
          return;
        }

        const sizes = await snapshot.getPromise(sizesState(viewportId));
        setZoomConfiguration(zoom);

        const parallelScale = convertZoomAndParallelScale(
          sizes,
          image,
          viewType,
          zoom,
          'zoomToParallel'
        );
        camera.setParallelScale(parallelScale);
        view.requestRender();
      },
    [image, setZoomConfiguration, view, viewportId, viewType]
  );
  return setZoom;
};

export const useSetParallelScaleToZoom = (): (() => Promise<void>) => {
  const setZoom = useSetZoom();
  const viewportId = useViewportId();
  const view = useContext(Contexts.ViewContext);
  const camera = useViewCamera(view);
  const image = useActiveSliceImage();
  const viewType = useViewType();

  const setParallelScaleToZoom = useRecoilCallback(
    ({ snapshot, set }) =>
      async () => {
        if (image == null) return;

        const sizes = await snapshot.getPromise(sizesState(viewportId));
        const parallelScale = camera.getParallelScale();
        const zoom = convertZoomAndParallelScale(
          sizes,
          image,
          viewType,
          parallelScale,
          'parallelToZoom'
        );
        set(scaleState(viewportId), parallelScale);
        setZoom(zoom);
      },
    [camera, image, setZoom, viewportId, viewType]
  );
  return setParallelScaleToZoom;
};

const performFlipOperation = (
  camera: vtkCamera,
  sliceBounds: number[],
  vertical: boolean
): { position: Vector3; focalPoint: Vector3; viewUp: Vector3 } => {
  const sliceCenter = [
    (sliceBounds[0] + sliceBounds[1]) / 2,
    (sliceBounds[2] + sliceBounds[3]) / 2,
    (sliceBounds[4] + sliceBounds[5]) / 2,
  ];

  const T = mat4.create();
  mat4.translate(T, T, sliceCenter as Vector3);

  const cameraViewUp = camera.getViewUp() as Vector3;
  const viewUpGl = vertical ? vec3.negate(vec3.create(), cameraViewUp) : cameraViewUp;

  if (vertical) {
    const left = vec3.create();
    const directionOfProjection = camera.getDirectionOfProjection();
    vec3.cross(left, viewUpGl, directionOfProjection as Vector3);
    mat4.rotate(T, T, Math.PI, left);
  } else {
    mat4.rotate(T, T, Math.PI, viewUpGl);
  }

  const negatedSliceCenter = vec3.negate(vec3.create(), sliceCenter as Vector3);
  mat4.translate(T, T, negatedSliceCenter);

  const position = vec3.create();
  const focalPoint = vec3.create();
  const cameraPosition = camera.getPosition();
  const cameraFocalPoint = camera.getFocalPoint();
  vec3.transformMat4(position, cameraPosition as Vector3, T);
  vec3.transformMat4(focalPoint, cameraFocalPoint as Vector3, T);

  return {
    position: [position[0], position[1], position[2]],
    focalPoint: [focalPoint[0], focalPoint[1], focalPoint[2]],
    viewUp: [viewUpGl[0], viewUpGl[1], viewUpGl[2]],
  };
};

export const useFlip = (): {
  flipVertical: (isVerticallyFlipped?: FlipOperation) => Promise<void>;
  flipHorizontal: (isHorizontallyFlipped?: FlipOperation) => Promise<void>;
} => {
  const viewportId = useViewportId();
  const view = useContext(Contexts.ViewContext);
  const viewType = useViewType();
  const setFlip = useSetFlip();
  const setViewingParameters = useSetViewingParameters();
  const { imagingProvider } = useImagingContext();

  const flipHorizontal = useRecoilCallback(
    ({ set, snapshot }) =>
      async (isHorizontallyFlipped: FlipOperation = null) => {
        const camera = view?.getCamera();
        const renderer = view?.getRenderer()?.get();
        const slicerBounds = await snapshot.getPromise(slicerBoundState(viewportId));

        const viewportDisplayConfigKey = imagingProvider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });

        if (camera == null || renderer == null || viewportDisplayConfigKey == null) return;

        const viewportDisplayConfig = await snapshot.getPromise(
          viewportDisplayConfigurationSelector(viewportDisplayConfigKey)
        );

        const xFlipped = viewportDisplayConfig?.isHorizontallyFlipped;

        // If the desired state is already flipped, do nothing
        if (xFlipped === isHorizontallyFlipped) return;

        if (slicerBounds != null) {
          const flipped = performFlipOperation(camera, slicerBounds, false);
          setViewingParameters(flipped);
          setFlip({
            isVerticallyFlipped: viewportDisplayConfig?.isVerticallyFlipped,
            isHorizontallyFlipped: !viewportDisplayConfig?.isHorizontallyFlipped,
          });
        }
      },
    [view, viewportId, setFlip, setViewingParameters, viewType, imagingProvider]
  );

  const flipVertical = useRecoilCallback(
    ({ set, snapshot }) =>
      async (isVerticallyFlipped: FlipOperation = null) => {
        const camera = view?.getCamera();
        const renderer = view?.getRenderer()?.get();
        const slicerBounds = await snapshot.getPromise(slicerBoundState(viewportId));

        const viewportDisplayConfigKey = imagingProvider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });

        if (camera == null || renderer == null || viewportDisplayConfigKey == null) return;

        const viewportDisplayConfig = await snapshot.getPromise(
          viewportDisplayConfigurationSelector(viewportDisplayConfigKey)
        );

        // If the desired state is already flipped, do nothing
        const yFlipped = viewportDisplayConfig?.isVerticallyFlipped;
        if (yFlipped === isVerticallyFlipped) return;

        if (slicerBounds != null) {
          const flipped = performFlipOperation(camera, slicerBounds, true);
          setViewingParameters(flipped);
          setFlip({
            isVerticallyFlipped: !viewportDisplayConfig?.isVerticallyFlipped,
            isHorizontallyFlipped: viewportDisplayConfig?.isHorizontallyFlipped,
          });
        }
      },
    [view, viewportId, setFlip, setViewingParameters, viewType, imagingProvider]
  );
  return { flipHorizontal, flipVertical };
};

export const useSetRoll = (): ((roll: number, rotate?: boolean) => Promise<void>) => {
  const view = useContext(Contexts.ViewContext);
  const viewType = useViewType();
  const setRotate = useSetRotate();
  const image = useActiveSliceImage();

  /*
   * Set the roll value to the camera, it takes an absolute value between 0 and 360.
   * NOTE: This doesn't shift the current roll value, it sets a new one.
   */
  const setRoll = useRecoilCallback(
    ({ snapshot, set }) =>
      async (newRoll: number, rotate: boolean = true) => {
        const camera = view?.getCamera();
        if (view == null || camera == null || image == null) return;

        const { viewUp, focalPoint } = calculateDefaultCameraParametersFromImageData(
          viewType,
          image
        );

        if (rotate && viewUp !== camera.getViewUp()) {
          const currentRoll = radiansToDegrees(angleBetweenVectors(viewUp, camera.getViewUp()));
          const difference: number = currentRoll - newRoll;
          rotateFromImageCenter(camera, difference, focalPoint);
          view.requestRender();
        }

        setRotate(newRoll);
      },
    [view, image, viewType, setRotate]
  );
  return setRoll;
};

const useSetFlip = () => {
  const viewportId = useViewportId();
  const viewType = useViewType();
  const { imagingProvider } = useImagingContext();

  const setFlipConfigurationDebounced = useRecoilCallback(
    ({ snapshot, set }) =>
      async (flip: {
        isHorizontallyFlipped: undefined | boolean;
        isVerticallyFlipped: undefined | boolean;
      }) => {
        const viewportDisplayConfigKey = imagingProvider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });
        if (viewportDisplayConfigKey == null) return;

        return set(viewportDisplayConfigurationSelector(viewportDisplayConfigKey), (config) => {
          return {
            ...config,
            ...flip,
          };
        });
      },
    [viewType, viewportId, imagingProvider]
  );
  return setFlipConfigurationDebounced;
};

const useSetRotate = () => {
  const viewportId = useViewportId();
  const viewType = useViewType();
  const { imagingProvider } = useImagingContext();

  const setRotateConfigurationDebounced = useRecoilCallback(
    ({ snapshot, set }) =>
      async (rotate: number) => {
        const viewportDisplayConfigKey = imagingProvider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });
        if (viewportDisplayConfigKey == null) return;
        set(viewportDisplayConfigurationSelector(viewportDisplayConfigKey), (config) => {
          return {
            ...config,
            params2d: {
              ...config?.params2d,
              rotate: Object.is(rotate, -0) ? 0 : rotate,
            },
          };
        });
      },
    [viewType, viewportId, imagingProvider]
  );
  return setRotateConfigurationDebounced;
};

export function useSetViewingParameters(): (arg1: {
  position: Vector3;
  focalPoint: Vector3;
  viewUp: Vector3;
}) => Promise<unknown> {
  const viewportId = useViewportId();
  const viewType = useViewType();
  const { imagingProvider } = useImagingContext();

  const setPositionConfiguration = useRecoilCallback(
    ({ snapshot, set }) =>
      async ({ position, focalPoint, viewUp }) => {
        const viewportDisplayConfigKey = imagingProvider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });
        if (viewportDisplayConfigKey == null) return;
        set(viewportDisplayConfigurationSelector(viewportDisplayConfigKey), (config) => {
          return {
            ...config,
            params2d: {
              ...config?.params2d,
              position,
              focalPoint,
              viewUp,
            },
          };
        });
      },
    [viewportId, viewType, imagingProvider]
  );
  return setPositionConfiguration;
}

const useSetZoomConfiguration = () => {
  const viewportId = useViewportId();
  const viewType = useViewType();
  const { imagingProvider } = useImagingContext();

  const setZoomConfiguration = useRecoilCallback(
    ({ snapshot, set }) =>
      async (zoom: number) => {
        const viewportDisplayConfigKey = imagingProvider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });
        if (viewportDisplayConfigKey == null) return;
        set(viewportDisplayConfigurationSelector(viewportDisplayConfigKey), (config) => {
          return {
            ...config,
            params2d: {
              ...config?.params2d,
              zoom: zoom,
            },
          };
        });
      },
    [viewportId, viewType, imagingProvider]
  );
  return setZoomConfiguration;
};

export const useResizeCamera: (viewportId: string) => () => Promise<void> = (viewportId) => {
  const view = useContext(Contexts.ViewContext);
  const viewType = useViewType();
  const setZoom = useSetZoom();
  const { imagingProvider } = useImagingContext();

  return useRecoilCallback(
    ({ snapshot, set }) =>
      async () => {
        const viewportDisplayConfigKey = imagingProvider?.getViewportDisplayConfigKey({
          viewType,
          viewportId,
        });
        if (view == null || viewType == null || viewportDisplayConfigKey == null) return;

        const viewportDisplayConfig = await snapshot.getPromise(
          viewportDisplayConfigurationSelector(viewportDisplayConfigKey)
        );
        setZoom(viewportDisplayConfig?.params2d?.zoom ?? DEFAULT_ZOOM_VALUE);
        view.requestRender();
      },
    [view, viewType, setZoom, imagingProvider, viewportId]
  );
};
